import $ from 'jquery';
import { cssAnimation } from '../../../util/animate-css.js';
import ShopEntity from './base.js';
import { triggerEvent } from '../../../util/dom.js';
// import mpro from '../mpro.init';

/**
 * @typedef {{ content: string|null; } } SectionResponse
 * @typedef {{ data_content: { [key: string]: string|null; } }} MultiSectionResponse
 * @typedef {{ url?: string; data?: any; showLoader?: boolean; animation?: boolean; focusElement?: string|HTMLElement; onSuccess?: string|((section: JQuery) => {}); ignoreErrors?: boolean; }} MpSectionOptions
 * @typedef {{ section: string, options?: MpSectionOptions; }} MpSectionIdObject
 * @typedef { string | MpSectionIdObject } MpSectionId
 * @typedef {{ data: MpSectionIdObject; sharedOptions?: MpSectionOptions }} MpSectionQueueData
 */

export default class Section extends ShopEntity {

	static get NAME() {
		return 'Section';
	}

	/** @type {MpSectionQueueData[]} */
	queue = [];
	/** @type {number} */
	timer;

	/**
	 * @param {MpSectionId[]} ids
	 * @param {MpSectionOptions} options
	 */
	reloadDelayed(ids, options = {}) {

		// Make sure we are not duplication the onSuccess callback
		const sharedOptions = { ...options };
		if (options.onSuccess) {
			delete options.onSuccess;
		}

		for (const idOrObject of ids) {

			const sectionData = (typeof idOrObject === 'object') ? idOrObject : { section: idOrObject };
			if (!sectionData.section) {
				console.warn('Cannot reload section for element (%o) - missing section id', this);
				continue;
			}

			this.addSectionToQueue({
				data: sectionData,
				sharedOptions,
			});
		}
	}

	/**
	 * @param {MpSectionQueueData} sectionData
	 */
	addSectionToQueue(sectionData) {
		this.queue.push(sectionData);
		this.timer && window.clearTimeout(this.timer);
		// The timer delay needs to be a little bigger in order for events to propagate properly (autocomplete events)
		this.timer = window.setTimeout(this.reloadSectionsNow.bind(this), 150);
	}

	/**
	 * Reload all queued sections
	 */
	reloadSectionsNow() {

		const sections = [];
		const pushedIds = new Set([]);
		let sharedOptions = {};

		for (const section of this.queue) {

			// These are the shared options
			if (section.sharedOptions) {
				sharedOptions = { ...sharedOptions, ...section.sharedOptions };
			}

			if (!section.data || !section.data.section) {
				console.warn('Cannot reload section for element (%o) - missing section id', this);
				continue;
			}

			/** @type {MpSectionIdObject} */// @ts-ignore
			const sectionData = section.data;

			// Skip duplicates
			if (pushedIds.has(sectionData.section.toString())) {
				continue;
			}

			sections.push(sectionData);
			pushedIds.add(sectionData.section.toString());
		}

		this.queue = [];

		// Do section reload
		this.reload(sections, sharedOptions);
	}

	/**
	 * @param {string|HTMLElement} container
	 */
	reloadContainer(container) {

		/** @type {JQuery} */
		// @ts-ignore
		const $container = $(container);

		// Trigger the section-reload event, the rest is handled by the event handler
		$container.triggerNative('mp-section-reload');
	}

	/**
	 * @param {MpSectionId[]} ids
	 * @param {MpSectionOptions} options
	 */
	async reload(ids, options = {}) {

		/** @type {Map<HTMLElement, { sectionId: string, options: MpSectionOptions }>} */
		let domSections = new Map;
		let $sortedSections = $();

		// Defaults for options
		if (!('showLoader' in options)) {
			options.showLoader = false;
		}
		if (!('animation' in options)) {
			options.animation = true;
		}

		const onSuccessMain = options.onSuccess;
		if (onSuccessMain) {
			delete options.onSuccess;
		}

		// Look for the section in the DOM and cache them (important, in case the DOM changes between requests)
		for (const [, section] of ids.entries()) {

			let sectionId;
			let sectionOpt = { ...options };
			let objectSectionOpt = {};

			if (typeof section === 'object') {
				if (section.options && typeof section.options === 'object') {
					objectSectionOpt = section.options;
				}
				sectionId = section.section;
			}
			else {
				sectionId = section;
			}

			let $currentSection = $(`[data-section-id="${sectionId}"]`);

			// Section not present in html
			if ($currentSection.length === 0) {
				console.warn('Current section not found in DOM: %o', sectionId);
				continue;
			}

			// Load only one section with 'transient_events'
			if (sectionId === 'transient_events') {
				$currentSection = $currentSection.last();
			}

			// In case we select multiple sections
			$currentSection.each((_, elem) => {

				// Add to section buffer (will be optimized later)
				// Note: The add method will keep unique DOM elements and will automatically sort them in DOM load order
				$sortedSections = $sortedSections.add(elem);

				// Merge options
				sectionOpt = { ...sectionOpt, ...objectSectionOpt };

				// Add to collection
				domSections.set(elem, { sectionId, options: sectionOpt });
			});
		}

		// Optimize the sections list so we keep only DOM sections that are not children of each other
		if ($sortedSections.length > 1) {
			$sortedSections = $sortedSections.filter((_, elem) => {
				const $col = $sortedSections.not(elem);
				const $elem = $(elem);
				// This section is already contained in another section
				if ($elem.closest($col).length > 0) {
					domSections.delete(elem);
					return false;
				}
				// Add loading animation
				mpro.addContentLoader($elem, (domSections.get(elem).options.showLoader === true) ? [] : ['none', 'transparent']);
				return true;
			});
		}

		// No valid ids
		if (domSections.size === 0) {
			return;
		}

		let $newSectionsCollection = $();

		// Get section data
		try {
			// Close tooltips/popovers
			mpro.closeSimpleOverlays();

			const respSections = (await mpro.ajax.section.reload([...domSections.values()].map(s => s.sectionId), options.url, options.data, options.ignoreErrors || false)).data_content;

			// Go through our sections
			for (const [sectionElement, { sectionId, options: sectionOpt }] of domSections) {

				const $currentSection = $(sectionElement);

				// Remove the loading animation
				mpro.removeContentLoader($currentSection);

				const sectionText = respSections[sectionId];

				// Invalid section
				if (sectionText == null) {
					console.warn('Invalid section id: %o', sectionId);
					continue;
				}

				// Get a reference of the new section
				const $newSection = $(sectionText);
				const hasContent = $newSection.length > 0;
				const hasMany = $newSection.length > 1;

				// Add to collection if we need to call the main onSuccess callback
				if (onSuccessMain) {
					$newSectionsCollection = $newSectionsCollection.add($newSection);
				}

				// Replace section
				// * Note
				// * jQuery will make sure scripts are executed ONLY once IF we don't use each() (even if the $currentSection collection has multiple elements)
				// * But, just to be 100% sure, the transient_events will be appended only once
				if (sectionId === 'transient_events') {
					$currentSection.last().replaceWith($newSection);
				}
				else {
					$currentSection.replaceWith($newSection);
				}

				// Do a fast entrance animation
				if (hasContent && sectionOpt.animation) {
					// eslint-disable-next-line unicorn/prefer-array-find
					const sectionElement = $newSection.filter('[data-section-id]')[0] || $newSection.filter((_i, v) => v.nodeType === 1)[0];
					if (sectionElement) {
						cssAnimation(sectionElement, 'fadeIn', 'faster');
					}
				}

				// Focus a certain element
				if (sectionOpt.focusElement) {
					// @ts-ignore
					mpro.scrollToElement($(sectionOpt.focusElement));
				}

				// Trigger section reload event
				triggerEvent(document, 'mp-section-did-reload', { bubbles: false, cancelable: false, detail: { $newSection, $oldSection: $currentSection } });

				// Re-init content plugins
				if (hasContent) {
					if (hasMany) {
						mpro.initStatics($newSection.wrapAll('<div>').parent());
						$newSection.unwrap();
					}
					else {
						mpro.initStatics($newSection);
					}
				}

				// Call the onSuccess callback
				if (sectionOpt.onSuccess) {
					if (typeof sectionOpt.onSuccess === 'function') {
						sectionOpt.onSuccess($newSection);
					}
					else if (typeof window[sectionOpt.onSuccess] === 'function') {
						window[sectionOpt.onSuccess]($newSection);
					}
				}
			}
		}
		catch (error) {

			console.warn('Error loading section data', error);

			// Remove all loading animations
			for (const [sectionElement, _sectionData] of domSections.entries()) {
				mpro.removeContentLoader($(sectionElement));
			}
		}

		// Call the main onSuccess callback
		if (onSuccessMain) {
			if (typeof onSuccessMain === 'function') {
				onSuccessMain($newSectionsCollection);
			}
			else if (typeof window[onSuccessMain] === 'function') {
				window[onSuccessMain]($newSectionsCollection);
			}
		}

		domSections = null;
	}
}
